import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiImageDetector = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-image-detector',
			name: 'AI Image Detector — Check If Photo Is AI Generated | Lift',
			description:
				'Detect AI generated images instantly with Lift AI Image Detector. Upload photo, check if image is AI generated, and get results with a confidence score.',
			url: 'https://liftapp.ai/ai-image-detector',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-image-detector#product' },
				{ '@id': 'https://liftapp.ai/ai-image-detector#howto' },
				{ '@id': 'https://liftapp.ai/ai-image-detector#benefits' },
				{ '@id': 'https://liftapp.ai/ai-image-detector#faq' },
				{ '@id': 'https://liftapp.ai/ai-image-detector#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-image-detector#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-image-detector#all-in-one' },
			],
		},
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-image-detector#product',
			name: 'AI Image Detector Online',
			description:
				'Check if a photo is AI-generated in seconds. Get an instant detection score with a clear confidence result.',
			url: 'https://liftapp.ai/ai-image-detector',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-image-detector#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photo',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'AI scans your image',
					text: 'Our AI image checker analyzes the photo for patterns, artifacts, and inconsistencies typical of AI-generated or manipulated images',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Get your result',
					text: 'See the AI-generated probability score as a percentage, along with a brief explanation to help you verify photo authenticity',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Try another image',
					text: 'Not sure about another photo? Upload a new image and run the AI detection check again.',
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-image-detector#benefits',
			name: 'Who can benefit from AI Image Detector?',
			description:
				'Lift AI Image Detector helps social media users, students, businesses, media professionals, and researchers verify photo authenticity and detect AI-generated or manipulated images.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Social Media Users — Spot fake AI photos in viral posts, suspicious profiles, and manipulated images before you share them.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Students — Avoid unintentionally using AI-generated images in assignments where authentic sources are required.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: "Businesses — Protect your brand by detecting AI-generated fakes that misuse your products, logo, or executives' likeness.",
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Media Professionals — Verify whether images circulating online are real before publishing.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Researchers & Fact-Checkers — Quickly assess whether suspicious images might be AI-generated or edited.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Lift AI Image Checker for social media users, journalists, educators, and businesses',
				description:
					'Lift AI Image Checker for social media users, journalists, educators, and businesses',
			},
		},
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-image-detector#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Image Detector?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Lift AI Image Detector is a online tool that checks whether a photo was created or manipulated using artificial intelligence. It analyzes uploaded images and returns a percentage-based confidence score, helping you verify photo authenticity — whether you're detecting AI-generated art, fake portraits, deepfake photos, or edited images.",
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Image Detector work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload a photo from your device. Lift's AI image checker scans the image for visual artifacts, unnatural patterns, and statistical inconsistencies commonly found in AI-generated or manipulated images. Within seconds, you'll see a result showing the probability that the image is AI-generated, along with a brief explanation of the finding.",
					},
				},
				{
					'@type': 'Question',
					name: 'How accurate is the AI image detector?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Image Detector uses advanced machine learning models trained to detect AI-generated images from tools like Midjourney, DALL·E, Stable Diffusion, and others. While no detector achieves 100% accuracy — especially as AI generation technology evolves — Lift delivers high-confidence results and is continuously updated to keep pace with the latest AI art and image generation methods.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe — do you store my images?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes, completely safe. Your images are processed securely and automatically deleted from our servers immediately after compression. We never store, share, or access your files — your privacy is our priority.',
					},
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-image-detector#why-lift',
			name: 'Why Lift for AI Image Detection',
			description:
				'Lift combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Detecting AI generated, photoshopped or edited images online | Lift AI tool',
				description:
					'Detecting AI generated, photoshopped or edited images online | Lift AI tool',
			},
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-image-detector#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-image-detector#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Image Detector',
					item: 'https://liftapp.ai/ai-image-detector',
				},
			],
		},
	],
};
